import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { productsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { US_STATES } from '../lib/utils';
import { toast } from 'sonner';
import { 
  Sparkles, 
  MapPin, 
  Package, 
  ArrowRight, 
  ArrowLeft,
  CheckCircle,
  Compass,
  Palette,
  Building2,
  BarChart3,
  Calendar
} from 'lucide-react';

const CRAFT_TYPES = [
  'Jewelry', 'Pottery/Ceramics', 'Woodworking', 'Textiles/Fiber Arts', 
  'Candles/Soap', 'Art/Paintings', 'Photography', 'Leather Goods',
  'Home Decor', 'Food/Baked Goods', 'Plants/Flowers', 'Mixed Media', 'Other'
];

const STEPS = [
  { id: 'welcome', title: 'Welcome', icon: Sparkles },
  { id: 'business', title: 'Your Business', icon: Palette },
  { id: 'location', title: 'Location', icon: MapPin },
  { id: 'product', title: 'First Product', icon: Package },
  { id: 'complete', title: 'All Set!', icon: CheckCircle },
];

export const Onboarding = ({ onComplete }) => {
  const { user, updateSettings } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Form state
  const [businessName, setBusinessName] = useState('');
  const [craftType, setCraftType] = useState('');
  const [homeCity, setHomeCity] = useState('');
  const [homeState, setHomeState] = useState('');
  const [homeZip, setHomeZip] = useState('');
  const [maxDistance, setMaxDistance] = useState(50);
  
  // Product form
  const [productName, setProductName] = useState('');
  const [productPrice, setProductPrice] = useState('');
  const [productCost, setProductCost] = useState('');

  const handleNext = async () => {
    if (currentStep === 1) {
      // Save business info
      try {
        await updateSettings({
          business_name: businessName || null,
          craft_type: craftType || null,
        });
      } catch (error) {
        toast.error('Failed to save');
        return;
      }
    } else if (currentStep === 2) {
      // Save location
      try {
        await updateSettings({
          home_city: homeCity || null,
          home_state: homeState || null,
          home_zip: homeZip || null,
          max_distance: parseInt(maxDistance) || 50,
        });
      } catch (error) {
        toast.error('Failed to save');
        return;
      }
    } else if (currentStep === 3 && productName) {
      // Create first product
      setLoading(true);
      try {
        await productsAPI.create({
          name: productName,
          retail_price: parseFloat(productPrice) || 0,
          total_cost: parseFloat(productCost) || 0,
          description: '',
          category: craftType || 'Other',
        });
        toast.success('Product created!');
      } catch (error) {
        toast.error('Failed to create product');
        setLoading(false);
        return;
      }
      setLoading(false);
    }
    
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleSkip = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleComplete = () => {
    localStorage.setItem('onboarding_complete', 'true');
    onComplete?.();
    navigate('/dashboard');
  };

  const renderStep = () => {
    switch (STEPS[currentStep].id) {
      case 'welcome':
        return (
          <div className="text-center space-y-6">
            <div className="w-24 h-24 mx-auto bg-gradient-to-br from-[#1E3A5F] to-[#1E3A5F] rounded-2xl flex items-center justify-center">
              <Sparkles className="w-12 h-12 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-[#1E3A5F] font-['Outfit']">
              Welcome to AMSCompass!
            </h2>
            <p className="text-lg text-gray-600 max-w-md mx-auto">
              Let us get you set up in just a few steps. This will help us personalize your experience.
            </p>
            <div className="grid grid-cols-2 gap-4 max-w-lg mx-auto mt-8">
              {[
                { icon: Calendar, label: 'Find Events', desc: 'Discover craft shows near you', link: '/events' },
                { icon: Building2, label: 'Track Boutiques', desc: 'Manage consignment sales', link: '/boutiques' },
                { icon: BarChart3, label: 'See Profits', desc: 'Know your true margins', link: '/analytics' },
                { icon: Compass, label: 'Plan Ahead', desc: 'Model your season', link: '/what-if' },
              ].map((item, i) => (
                <button
                  key={i}
                  onClick={() => {
                    localStorage.setItem('onboarding_complete', 'true');
                    navigate(item.link);
                  }}
                  className="p-4 bg-[#FFEDDF] rounded-xl text-left hover:bg-[#FFE4D0] hover:shadow-md transition-all cursor-pointer border-2 border-transparent hover:border-[#1E3A5F]"
                  data-testid={`onboard-quick-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="w-6 h-6 text-[#1E3A5F] mb-2" />
                  <p className="font-semibold text-[#1E3A5F]">{item.label}</p>
                  <p className="text-xs text-gray-600">{item.desc}</p>
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-4 text-center">
              Click any feature to skip setup and start using it now
            </p>
          </div>
        );
        
      case 'business':
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <Palette className="w-12 h-12 text-[#1E3A5F] mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-[#1E3A5F] font-['Outfit']">
                Tell us about your business
              </h2>
              <p className="text-gray-600">This helps us recommend relevant events</p>
            </div>
            <div className="space-y-4 max-w-md mx-auto">
              <div>
                <Label className="text-[#1E3A5F] font-medium">Business Name</Label>
                <Input
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  placeholder="e.g., Jane's Handmade Jewelry"
                  className="mt-1"
                  data-testid="onboard-business-name"
                />
              </div>
              <div>
                <Label className="text-[#1E3A5F] font-medium">What do you create?</Label>
                <Select value={craftType} onValueChange={setCraftType}>
                  <SelectTrigger className="mt-1" data-testid="onboard-craft-type">
                    <SelectValue placeholder="Select your craft type" />
                  </SelectTrigger>
                  <SelectContent>
                    {CRAFT_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );
        
      case 'location':
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <MapPin className="w-12 h-12 text-[#FE5F55] mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-[#1E3A5F] font-['Outfit']">
                Where are you based?
              </h2>
              <p className="text-gray-600">We will find events near you</p>
            </div>
            <div className="space-y-4 max-w-md mx-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#1E3A5F] font-medium">City</Label>
                  <Input
                    value={homeCity}
                    onChange={(e) => setHomeCity(e.target.value)}
                    placeholder="Austin"
                    className="mt-1"
                    data-testid="onboard-city"
                  />
                </div>
                <div>
                  <Label className="text-[#1E3A5F] font-medium">State</Label>
                  <Select value={homeState} onValueChange={setHomeState}>
                    <SelectTrigger className="mt-1" data-testid="onboard-state">
                      <SelectValue placeholder="State" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {US_STATES.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-[#1E3A5F] font-medium">ZIP Code</Label>
                <Input
                  value={homeZip}
                  onChange={(e) => setHomeZip(e.target.value)}
                  placeholder="78701"
                  className="mt-1 max-w-[150px]"
                  data-testid="onboard-zip"
                />
              </div>
              <div>
                <Label className="text-[#1E3A5F] font-medium">How far will you travel for events? (miles)</Label>
                <Input
                  type="number"
                  value={maxDistance}
                  onChange={(e) => setMaxDistance(e.target.value)}
                  placeholder="50"
                  className="mt-1 max-w-[150px]"
                  data-testid="onboard-distance"
                />
              </div>
            </div>
          </div>
        );
        
      case 'product':
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <Package className="w-12 h-12 text-[#1E3A5F] mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-[#1E3A5F] font-['Outfit']">
                Add your first product
              </h2>
              <p className="text-gray-600">You can add more products later</p>
            </div>
            <div className="space-y-4 max-w-md mx-auto">
              <div>
                <Label className="text-[#1E3A5F] font-medium">Product Name</Label>
                <Input
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  placeholder="e.g., Handmade Silver Earrings"
                  className="mt-1"
                  data-testid="onboard-product-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#1E3A5F] font-medium">Retail Price ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={productPrice}
                    onChange={(e) => setProductPrice(e.target.value)}
                    placeholder="45.00"
                    className="mt-1"
                    data-testid="onboard-product-price"
                  />
                </div>
                <div>
                  <Label className="text-[#1E3A5F] font-medium">Material Cost ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={productCost}
                    onChange={(e) => setProductCost(e.target.value)}
                    placeholder="12.00"
                    className="mt-1"
                    data-testid="onboard-product-cost"
                  />
                </div>
              </div>
              {productName && productPrice && productCost && (
                <div className="p-4 bg-green-50 rounded-xl">
                  <p className="text-sm text-gray-600">Estimated profit margin:</p>
                  <p className="text-2xl font-bold text-green-600">
                    {Math.round((1 - parseFloat(productCost) / parseFloat(productPrice)) * 100)}%
                  </p>
                </div>
              )}
            </div>
          </div>
        );
        
      case 'complete':
        return (
          <div className="text-center space-y-6">
            <div className="w-24 h-24 mx-auto bg-green-500 rounded-full flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-[#1E3A5F] font-['Outfit']">
              You are all set!
            </h2>
            <p className="text-lg text-gray-600 max-w-md mx-auto">
              Your account is ready. Start discovering events, adding products, and tracking your sales.
            </p>
            <div className="flex flex-col gap-3 max-w-xs mx-auto mt-8">
              <Button onClick={handleComplete} className="btn-primary" data-testid="onboard-complete">
                Go to Dashboard
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button variant="outline" onClick={() => navigate('/ai-discover')}>
                Find Events with AI Scout
              </Button>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFFFF] flex flex-col">
      {/* Progress Bar */}
      <div className="bg-white border-b p-4">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            {STEPS.map((step, i) => (
              <div
                key={step.id}
                className={`flex items-center ${i < STEPS.length - 1 ? 'flex-1' : ''}`}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    i <= currentStep
                      ? 'bg-[#1E3A5F] text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {i < currentStep ? <CheckCircle className="w-4 h-4" /> : i + 1}
                </div>
                {i < STEPS.length - 1 && (
                  <div
                    className={`flex-1 h-1 mx-2 ${
                      i < currentStep ? 'bg-[#1E3A5F]' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-gray-600">
            Step {currentStep + 1} of {STEPS.length}: {STEPS[currentStep].title}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-2xl">
          {renderStep()}
        </div>
      </div>

      {/* Navigation */}
      {currentStep < STEPS.length - 1 && (
        <div className="bg-white border-t p-4">
          <div className="max-w-3xl mx-auto flex justify-between">
            <Button
              variant="ghost"
              onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
              disabled={currentStep === 0}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex gap-2">
              {currentStep > 0 && currentStep < 4 && (
                <Button variant="ghost" onClick={handleSkip}>
                  Skip
                </Button>
              )}
              <Button onClick={handleNext} className="btn-primary" disabled={loading}>
                {loading ? 'Saving...' : currentStep === 3 ? (productName ? 'Save & Continue' : 'Skip') : 'Continue'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Onboarding;
